import type { Feature } from '../types';

// -- Types --

export interface FeatureNode {
  root: Feature;
  children: Feature[];
}

export interface FeatureFilterNode {
  feature: Feature;
  children: Feature[];
}

export interface FeatureGroup {
  rootLabel: string;
  subCount: number;
  subLabels: string[];
}

// -- Builders --

/**
 * Build a tree of root features (category_id == null) with their children.
 */
export function buildFeatureTree(features: Feature[]): FeatureNode[] {
  const childrenMap = new Map<number, Feature[]>();

  for (const f of features) {
    if (f.category_id != null) {
      const list = childrenMap.get(f.category_id) || [];
      list.push(f);
      childrenMap.set(f.category_id, list);
    }
  }

  return features
    .filter(f => f.category_id == null)
    .map(root => ({ root, children: childrenMap.get(root.id) || [] }));
}

/**
 * Build a map of feature id to Feature for quick lookup.
 */
export function buildFeatureMap(allFeatures: Feature[]): Map<number, Feature> {
  const map = new Map<number, Feature>();
  for (const f of allFeatures) map.set(f.id, f);
  return map;
}

/**
 * Group a user's feature ids by root category.
 */
export function groupUserFeatures(
  userFeatureIds: number[],
  featureMap: Map<number, Feature>,
): FeatureGroup[] {
  const groups = new Map<number, number[]>();
  const rootsInOrder: number[] = [];

  for (const fid of userFeatureIds) {
    const feat = featureMap.get(fid);
    if (!feat) continue;

    if (feat.category_id == null) {
      if (!groups.has(fid)) {
        groups.set(fid, []);
        rootsInOrder.push(fid);
      }
    } else {
      const parentId = feat.category_id;
      if (!groups.has(parentId)) {
        groups.set(parentId, []);
        rootsInOrder.push(parentId);
      }
      groups.get(parentId)!.push(fid);
    }
  }

  return rootsInOrder.map(rootId => {
    const rootFeat = featureMap.get(rootId);
    const children = groups.get(rootId) || [];
    return {
      rootLabel: rootFeat?.label ?? `Feature #${rootId}`,
      subCount: children.length,
      subLabels: children.map(cid => featureMap.get(cid)?.label ?? '').filter(Boolean),
    };
  });
}

/**
 * Build a map from root feature id to its child feature ids.
 */
export function getChildIdsByRoot(tree: FeatureNode[]): Map<number, number[]> {
  const map = new Map<number, number[]>();
  for (const { root, children } of tree) {
    map.set(root.id, children.map(c => c.id));
  }
  return map;
}
